import React, { useEffect, useRef, useState } from "react";
import { useA11y } from "../a11y/AccessibilityContext";
import { canNarrate, narrateText, stopNarration } from "../a11y/narrator";

export default function AccessibilityToolbar() {
  const { theme, fontScale, narrateOn, toggleTheme, zoomIn, zoomOut, resetZoom, toggleNarration } = useA11y();

  // show toolbar when: at top OR scrolling up
  const [visible, setVisible] = useState(true);
  const lastY = useRef(0);

  useEffect(() => {
    lastY.current = window.scrollY || 0;

    const onScroll = () => {
      const y = window.scrollY || 0;

      // Always show at the top
      if (y <= 10) {
        setVisible(true);
        lastY.current = y;
        return;
      }

      // If scrolling up -> show, scrolling down -> hide
      if (y < lastY.current) setVisible(true);
      else if (y > lastY.current) setVisible(false);

      lastY.current = y;
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNarratePage = () => {
    const willTurnOn = !narrateOn;
    toggleNarration();

    if (willTurnOn) {
      const main = document.getElementById("main-content");
      if (!main) return;

      // Only meaningful content from sections
      const readableElements = main.querySelectorAll(
        "section h1, section h2, section h3, section h4, section h5, section h6, section p, section li"
      );

      let combinedText = "";
      readableElements.forEach((el) => {
        const text = el.innerText?.trim();
        if (text) combinedText += text + ". ";
      });

      narrateText(combinedText);
    } else {
      stopNarration();
    }
  };

  const zoomPercent = Math.round(fontScale * 100);

  return (
    <div
      className={`a11y-toolbar ${visible ? "a11y-toolbar--show" : "a11y-toolbar--hide"}`}
      role="region"
      aria-label="Accessibility controls"
    >
      <button type="button" className="a11y-btn" onClick={zoomOut} aria-label="Zoom out">
        A-
      </button>

      <button
        type="button"
        className="a11y-btn"
        onClick={resetZoom}
        aria-label={`Reset zoom (currently ${zoomPercent} percent)`}
      >
        {zoomPercent}%
      </button>

      <button type="button" className="a11y-btn" onClick={zoomIn} aria-label="Zoom in">
        A+
      </button>

      <button type="button" className="a11y-btn" onClick={toggleTheme} aria-label="Toggle dark mode">
        {theme === "dark" ? "Light" : "Dark"}
      </button>

      <button
        type="button"
        className="a11y-btn"
        onClick={handleNarratePage}
        aria-label="Narrate main page content"
        disabled={!canNarrate()}
        title={!canNarrate() ? "Narration not supported in this browser" : ""}
      >
        {narrateOn ? "Stop" : "Narrate"}
      </button>
    </div>
  );
}